import { type MutableRefObject, useRef } from 'react';
import { ICE_CONNECTED_TIMEOUT } from '../constants';
import type { UseVoiceLiveTelemetryResult } from './useVoiceLiveTelemetry';

export interface UseIceConnectedTimeoutOptions {
  /** Authenticated user id (telemetry only). */
  userId: string;
  /** Telemetry sink. */
  telemetry: UseVoiceLiveTelemetryResult;
  /**
   * Current peer connection ref — used to guard against stale peer events
   * (e.g. a cleanup that replaced the peer between schedule and fire).
   */
  pcRef: MutableRefObject<RTCPeerConnection | null>;
  /** Called when ICE connectivity checks stall past the deadline. */
  onInitialFailure: (reason: string) => void;
}

export interface UseIceConnectedTimeoutResult {
  /**
   * Start the ICE connectivity-check deadline for the given peer connection.
   * Cancels any previously running deadline first (idempotent).
   */
  startIceConnectedTimeout: (pc: RTCPeerConnection) => void;
  /** Cancel any pending ICE connectivity-check deadline. */
  cancelIceConnectedTimeout: () => void;
}

/**
 * Manages the ICE connectivity-check deadline: a timer started after
 * setRemoteDescription that fires onInitialFailure if the peer connection
 * doesn't reach 'connected'/'completed' within ICE_CONNECTED_TIMEOUT ms.
 *
 * This catches the "ICE stuck in checking" case caused by 701 STUN/TURN
 * host-lookup failures where the browser never declares iceConnectionState
 * 'failed' within a reasonable time on its own.
 */
export const useIceConnectedTimeout = ({
  userId,
  telemetry,
  pcRef,
  onInitialFailure,
}: UseIceConnectedTimeoutOptions): UseIceConnectedTimeoutResult => {
  const timeoutRef = useRef<number | null>(null);

  const cancelIceConnectedTimeout = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startIceConnectedTimeout = (pc: RTCPeerConnection) => {
    cancelIceConnectedTimeout();
    timeoutRef.current = window.setTimeout(() => {
      // Ignore if the peer was replaced or cleaned up between schedule and fire.
      if (pcRef.current !== pc) return;
      if (pc.iceConnectionState !== 'connected' && pc.iceConnectionState !== 'completed') {
        console.warn('⚠️ ICE connectivity check timed out (ice-connected-timeout)');
        telemetry.track('WebRTC:IceConnectedTimeout', {
          iceConnectionState: pc.iceConnectionState,
          iceServerUrls: telemetry.iceServerUrls(),
          userId,
        });
        onInitialFailure('ice-connected-timeout');
      }
    }, ICE_CONNECTED_TIMEOUT);
  };

  return { startIceConnectedTimeout, cancelIceConnectedTimeout };
};
